import * as React from 'react';
import { FC, useEffect, useRef, useCallback, useContext } from 'react';
import { onKeyDownCallback } from '../../../../../../utils/onKeyDown';
import { ISettings } from '../../../../../../types';
import { StoreContext } from '../../../../../../services/store';

interface ProductImageProps {
  imageLink?: string;
  title: string;
  cornerType: ISettings['thumbnail_corners'];
  borderRadius: string;
  onImageClick: () => void;
  containerHeight?: number;
  containerWidth?: number;
  customButtons?: JSX.Element[] | undefined;
  productId: string;
  mode: 'modal' | 'list';
}

const ProductImage: FC<ProductImageProps> = ({
  imageLink,
  title,
  cornerType,
  borderRadius,
  onImageClick,
  containerHeight,
  containerWidth,
  customButtons,
  productId,
  mode,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const context = useContext(StoreContext);
  const setImageWidth = context.setProductImageWidth;

  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const aspectRatio = img.naturalWidth / img.naturalHeight;

    let scaledWidth = containerWidth || 0;
    let scaledHeight = containerHeight || 0;

    if (mode === 'modal' && containerHeight) {
      // For modal = calculate width based on fixed height and aspect ratio
      scaledWidth = containerHeight * aspectRatio;
    } else if (mode === 'list' && containerWidth) {
      // For list : calculate height based on fixed width and aspect ratio
      scaledHeight = containerWidth / aspectRatio;
    }

    setImageWidth(productId, scaledWidth);
  }, [mode, containerHeight, containerWidth, setImageWidth, productId]);

  // Check if image is already loaded when component mounts or dimensions change
  useEffect(() => {
    if (containerRef.current) {
      const img = containerRef.current.querySelector('img');
      if (img && img.complete) {
        handleImageLoad({ currentTarget: img } as React.SyntheticEvent<HTMLImageElement>);
      }
    }
  }, [containerHeight, containerWidth, handleImageLoad]);

  // memoize the style to prevent object recreation on each render
  const imageStyle = React.useMemo(() => ({
    ...styles.image(mode, containerHeight, containerWidth),
    borderRadius: cornerType === 'rounded' ? borderRadius : '0px',
  }), [mode, containerHeight, containerWidth, cornerType, borderRadius]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDownCallback(e, onImageClick);
  }, [onImageClick]);

  return (
    <div
      ref={containerRef}
      style={styles.imageContainer}
      onClick={onImageClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="link"
      aria-label={`go to the ${title}'s product page`}
    >
      {imageLink && (
        <img
          src={imageLink}
          alt={title}
          onLoad={handleImageLoad}
          style={imageStyle}
        />
      )}
      {customButtons}
    </div>
  );
};

// Static styles object to prevent recreation
const styles = {
  imageContainer: {
    display: 'block',
    position: 'relative' as const,
    cursor: 'pointer',
  },
  image: (mode: 'modal' | 'list', containerHeight?: number, containerWidth?: number) => ({
    display: 'block',
    height: mode === 'modal' ? (containerHeight || 'auto') : 'auto',
    width: mode === 'list' ? (containerWidth || 'auto') : 'auto',
    maxWidth: '100%',
    maxHeight: '100%',
  }),
} as const;

export default ProductImage;